/**
 * ShareModal — the one canonical "share a link" handoff for the portal.
 *
 * One link field + Copy, and WhatsApp / SMS / Email / Open actions, with the
 * "no number on file" manual-number fallback. Replaces the two near-identical
 * implementations (ProposalShareModal's Estimator-local proposal-* classes —
 * the page-local-class trap from the drift audit — and the inline
 * ContractShareModal). Built on the global Modal shell + form-* / btn-* classes
 * and the shared messaging helpers. No bespoke modal, no page-local classes.
 *
 * The message is the recipient's to read, so the caller supplies buildMessage
 * (it owns the wording / language). `children` is optional extra body content
 * (e.g. an expiry editor); `footerExtra` is an optional extra footer button.
 */

import { useState, type ReactNode } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import { formatPhoneE164, getSmsDeepLink, getWhatsAppDeepLink } from '../lib/messaging';
import Modal from './Modal';

interface ShareModalProps {
  /** Header title, e.g. "Share Proposal" or "Share agreement · CONTRACT-ZA-00012". */
  title: string;
  /** The link to share. */
  url: string | null | undefined;
  recipientName?: string | null;
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  /** Builds the WhatsApp/SMS/Email body. Caller owns the wording + language. */
  buildMessage: (firstName: string, url: string) => string;
  /** Email subject line. */
  emailSubject: string;
  /** Label above the read-only link field. Default "Link". */
  linkLabel?: string;
  /** Label for the manual-number field shown when no number is on file. */
  phoneFieldLabel?: string;
  /** Extra body content above the send actions (e.g. a reference badge, expiry editor). */
  children?: ReactNode;
  /** Extra footer button shown before Close/Copy (e.g. a "Done" action). */
  footerExtra?: ReactNode;
  onClose: () => void;
}

export default function ShareModal({
  title,
  url,
  recipientName,
  recipientEmail,
  recipientPhone,
  buildMessage,
  emailSubject,
  linkLabel = 'Link',
  phoneFieldLabel = 'WhatsApp / mobile number',
  children,
  footerExtra,
  onClose,
}: ShareModalProps) {
  const { countryConfig } = useConfig();
  const phonePrefix = (countryConfig as unknown as Record<string, string>)?.phone_prefix;
  const [copied, setCopied] = useState(false);
  // Operator-entered recipient number when none is on file.
  const [manualPhone, setManualPhone] = useState('');

  // Recipient: the number on file, else one typed here. No number means no
  // direct chat, so WhatsApp/SMS stay blocked rather than opening the picker.
  const storedPhone = formatPhoneE164(recipientPhone, phonePrefix);
  const typedPhone = formatPhoneE164(manualPhone, phonePrefix);
  const effectivePhone = storedPhone || typedPhone;

  const message = (): string => buildMessage((recipientName || '').split(' ')[0] || 'there', url || '');

  function copyUrl() {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  function sendViaWhatsApp() {
    if (!url || !effectivePhone) return;
    window.open(getWhatsAppDeepLink(effectivePhone, message()), '_blank');
  }
  function sendViaSms() {
    if (!url || !effectivePhone) return;
    window.location.href = getSmsDeepLink(effectivePhone, message());
  }
  function sendViaEmail() {
    if (!url) return;
    const to = recipientEmail || '';
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(message())}`;
  }

  return (
    <Modal
      title={title}
      size="sm"
      onClose={onClose}
      footer={
        <>
          {footerExtra}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          <button type="button" className={`btn ${copied ? 'btn-success' : 'btn-primary'}`} onClick={copyUrl}>
            {copied ? '✓ Copied' : 'Copy link'}
          </button>
        </>
      }
    >
      {children}

      <div className="form-group">
        <label className="form-label">{linkLabel}</label>
        <input className="form-input" value={url || ''} readOnly onFocus={(e) => e.target.select()} />
      </div>

      {!storedPhone && (
        <div className="form-group">
          <label className="form-label">{phoneFieldLabel}</label>
          <input
            className="form-input"
            type="tel"
            value={manualPhone}
            onChange={(e) => setManualPhone(e.target.value)}
            placeholder="e.g. 071 234 5678"
          />
          <span className="form-help">
            No number on file, so WhatsApp and SMS are off. Add their number to message them directly. Email and copy always work.
          </span>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Send via</label>
        <div className="flex flex-col gap-2">
          <button type="button" className="btn btn-whatsapp w-full" onClick={sendViaWhatsApp} disabled={!effectivePhone}>💬 WhatsApp</button>
          <button type="button" className="btn btn-primary w-full" onClick={sendViaSms} disabled={!effectivePhone}>📲 SMS</button>
          <button type="button" className="btn btn-outline w-full" onClick={sendViaEmail}>📧 Email</button>
          {url && <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-outline w-full">🔗 Open link</a>}
        </div>
      </div>
    </Modal>
  );
}
